/**
 * Send the user to another form.
 *
 * Use this — not `navigate('/m/<id>')` — whenever one page sends the user to a
 * different form ("Open Service Allocation" from a request, "List view" from
 * the schedule).
 *
 * WHY: the URL is not what moves the shell. `AppShell` chooses the rendered
 * form from its own `page` state, and its `open()` is what sets it — along with
 * closing the drawer, expanding the nav group so the sidebar highlights where
 * the user now is, and pushing the URL. A bare `navigate()` does only the last
 * of those, which leaves the address bar on the new form and the screen on the
 * old one.
 *
 * A window event rather than a prop, matching `ai:nav-collapse`: the pages that
 * need this are several components deep (a section inside a detail page inside
 * a list page), and threading a callback down that chain to every one of them
 * would be worse than the one listener AppShell already keeps.
 */
export function navigateToPage(pageId: string, recordId?: string, opts?: NavigateOptions): void {
  // Set BEFORE the event: AppShell handles it synchronously, so the target page
  // mounts inside this call and must already be able to read the handoff.
  pending = recordId ? { page: pageId, recordId, context: opts?.context ?? {} } : null;
  if (opts?.returnTo) setReturnOrigin(opts.returnTo);
  window.dispatchEvent(new CustomEvent('ai:navigate', {
    detail: { page: pageId, onlyIfAllowed: opts?.onlyIfAllowed === true },
  }));
}

/** Options for {@link navigateToPage}. */
export interface NavigateOptions {
  /** Move only when the user can see the target form in the sidebar (role permission + Form Setup);
   *  otherwise nothing happens. For an AUTOMATIC hop the user did not ask for — e.g. SDMS → Analysis
   *  Queue after a Data Package Submit, where OG checks the user's navigation item first
   *  (DataPackageViewController.NavigateToAnalysisQueue). A link the user clicked keeps the default. */
  onlyIfAllowed?: boolean;
  /** Extra detail for the target form, read with {@link consumePendingNavigation} (needs a recordId). */
  context?: Record<string, string>;
  /** Where closing the target form goes back to — see {@link returnToOrigin}. */
  returnTo?: ReturnOrigin;
}

/** The record id that asks a form to start a new record instead of opening one. */
export const NEW_RECORD = '__new__';

/** The page a form was opened from, so closing the form goes back there (My Jobs ▸ Resume). */
export interface ReturnOrigin {
  /** Nav id to go back to. */
  page: string;
  /** Its caption, for the "Back to …" button. */
  label: string;
  /** What the user was working on, shown beside the button. */
  detail?: string;
  /** Reopen this record on the way back (read by the page like any navigateToPage handoff), so
   *  the user lands on what they left rather than the page's list — e.g. the PLM batch a Daily QC
   *  was opened from (owner 2026-11-03). Omit to land on the page as usual. */
  recordId?: string;
  /** Extra detail for that record, read with {@link consumePendingNavigation}. */
  context?: Record<string, string>;
}

/**
 * The record a cross-page navigation asked the target form to OPEN, if any.
 *
 * Standing rule 10 says a form opens on its list — that governs clicking a
 * NAVIGATION entry, where no record has been chosen. Following a link from one
 * record to a related one ("Open request" on a service) is the "select a row"
 * step of List → select → detail, performed from somewhere else; landing on the
 * list and making the user find the row they just came from would lose the
 * thing they clicked.
 *
 * A module-level handoff rather than a prop or an event payload, for the reason
 * the whole file exists: AppShell renders every form from one switch, and
 * teaching it to pass a record id into ONE page would make the shell know that
 * page's internals. The target reads it on mount instead.
 *
 * CONSUMED, not read: it clears on the first call, so navigating to the same
 * form again later — from the sidebar, or by re-clicking the active item —
 * lands on the list as rule 10 requires, rather than reopening a stale record.
 */
export function consumePendingRecord(pageId: string): string | null {
  return consumePendingNavigation(pageId)?.recordId ?? null;
}

/**
 * Read the handoff WITHOUT clearing it, for a useState initializer. React's StrictMode
 * renders a mounting component twice, so an initializer that consumed the handoff got it
 * only on the render React throws away, and the form opened on its list. Clear it from
 * an effect after mount with {@link consumePendingNavigation}.
 */
export function peekPendingNavigation(pageId: string): { recordId: string; context: Record<string, string> } | null {
  if (!pending || pending.page !== pageId) return null;
  return { recordId: pending.recordId, context: pending.context };
}

/** {@link consumePendingRecord}, with the extra detail the navigation carried. */
export function consumePendingNavigation(pageId: string): { recordId: string; context: Record<string, string> } | null {
  if (!pending || pending.page !== pageId) return null;
  const { recordId, context } = pending;
  pending = null;
  return { recordId, context };
}

/**
 * Drop a handoff whose navigation did NOT happen.
 *
 * AppShell's open() can refuse the move — it asks "Unsaved changes will be
 * lost. Do you want to proceed?" first, and the user can say no. The record
 * was already parked by then, so without this it would survive and hijack the
 * NEXT visit to that form from the sidebar, opening a record the user did not
 * pick and breaking standing rule 10.
 */
export function clearPendingRecord(): void { pending = null; }

/** The pending handoff, if a navigation asked for one. Module-level because it
 *  must survive the unmount of the page that set it. */
let pending: { page: string; recordId: string; context: Record<string, string> } | null = null;

let origin: ReturnOrigin | null = null;
const originListeners = new Set<() => void>();

function setReturnOrigin(next: ReturnOrigin | null): void {
  if (origin === next) return;
  origin = next;
  originListeners.forEach((l) => l());
}

/** The form's return path, if it was opened with `returnTo`. */
export function getReturnOrigin(): ReturnOrigin | null { return origin; }

/** Forget the return path: the user has gone back, or moved on by their own choice. */
export function clearReturnOrigin(): void { setReturnOrigin(null); }

/** Subscribe to return-path changes (for useSyncExternalStore). */
export function subscribeReturnOrigin(listener: () => void): () => void {
  originListeners.add(listener);
  return () => { originListeners.delete(listener); };
}

/**
 * Go back to the page this form was opened from. A form's Close calls this first and
 * does its normal close only when it returns false, so a form opened any other way
 * behaves exactly as before. AppShell clears the path once the move really happens,
 * so answering "stay" to the unsaved-changes prompt leaves it in place.
 */
export function returnToOrigin(): boolean {
  if (!origin) return false;
  // Park the record to reopen BEFORE the event, as navigateToPage does (AppShell handles it synchronously).
  pending = origin.recordId ? { page: origin.page, recordId: origin.recordId, context: origin.context ?? {} } : null;
  window.dispatchEvent(new CustomEvent('ai:navigate', { detail: { page: origin.page, onlyIfAllowed: false } }));
  return true;
}
